import { Component, ElementRef, inject, Input, OnInit, Renderer2, ViewChild } from '@angular/core'
import { IncomingPayroll } from '../../../../store/payroll/payroll.state'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { CommonModule, DatePipe } from '@angular/common'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { environment } from '../../../../../environments/environment'

export interface PayslipEarningComponent {
  name: string
  fullAmount: number
  proratedAmount: number
}

export interface PayslipDeductionComponent {
  name: string
  fullAmount: number
  proratedAmount: number
}

export interface PayslipData {
  employeeId: string
  employeeName: string
  payPeriod: string
  daysInMonth: number
  lopDays: number
  payableDays: number

  earnings: {
    basic: PayslipEarningComponent
    hra: PayslipEarningComponent
    specialAllowance: PayslipEarningComponent // Explicitly listed
    otherComponents: PayslipEarningComponent[] // For customEarnings and reimbursements from otherComponents list
    bonus: number
    expenses: number
    totalEarnings: number
  }

  deductions: {
    pfEmployeeContribution: PayslipDeductionComponent | null
    otherComponentDeductions: PayslipDeductionComponent[] // For customDeductions and employee contributions from earning components
    tds: number
    totalDeductions: number
  }

  netSalaryBeforeAdjustments: number
  adjustments: number
  rounding: number
  netPayableSalary: number

  backendCalculatedGross: number
  backendCalculatedNet: number
}

@Component({
  selector: 'app-payslip',
  imports: [CommonModule],
  templateUrl: './payslip.component.html',
  styleUrl: './payslip.component.css',
  providers: [DatePipe]
})
export class PayslipComponent implements OnInit {
  private activeModal = inject(NgbActiveModal)
  private renderer = inject(Renderer2)
  @Input() payrollData!: IncomingPayroll
  @Input() payPeriodStartDate!: string
  @Input() download = false
  @ViewChild('payslipContainerToExport') payslipContainerToExport!: ElementRef
  compliancesListEntries: [string, boolean][] = []
  //payslipData: PayslipData | null = null
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  // imageBaseUrl = 'http://localhost:3000/uploads/1748597209118-160190611.png'

  ngOnInit(): void {
    if (!this.payrollData || !this.payrollData.calculationBreakdown) {
      // Optionally close the modal or show an error message.
      this.activeModal.close()
    }
    // console.log('this.payrollData.calculationBreakdown ', this.payrollData.calculationBreakdown)
  }

  onClose(): void {
    this.activeModal.close()
  }
  // Helper to count days between two dates (inclusive)
  countDaysInclusive(startDate: Date, endDate: Date): number {
    if (startDate > endDate) return 0
    const start = Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth(),
      startDate.getUTCDate()
    )
    const end = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate())
    return (end - start) / (1000 * 60 * 60 * 24) + 1
  }

  // get businessLocation(): string {
  //   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //   const city = (this.payrollData.employee.business as any).city
  //   return `${city.name}, ${city.state.name}, ${city.country_code}`
  // }

  //gps
  //gps
  get businessLocation(): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const city = (this.payrollData.employee.business as any)?.city

    return city
      ? `${city.name ?? ''}, ${city.state?.name ?? ''}, ${city.country_code ?? ''}`.replace(
          /(^, |, , |, $)/g,
          ''
        )
      : '—'
  }

  get totalDeductions(): number {
    return (
      (this.payrollData.calculationBreakdown?.deductions.reduce(
        (sum, comp) => sum + comp.amount,
        0
      ) ?? 0) + this.payrollData.tdsAfterCess
    )
  }

  // get daysInMonth(): number {
  //   const date = new Date(this.payPeriodStartDate + 'T00:00:00Z')

  //   // Day 0 of the next month in UTC gives the last day of the current month
  //   return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()
  // }

  get daysInMonth(): number {
    const effectiveFromDate = this.payrollData.salary?.effectiveFrom
      ? new Date(this.payrollData.salary.effectiveFrom + 'T00:00:00Z')
      : null

    const payPeriodDate = new Date(this.payPeriodStartDate + 'T00:00:00Z')

    let targetDate: Date

    // ✅ Use effectiveFrom if it's in the same month/year as pay period
    if (
      effectiveFromDate &&
      effectiveFromDate.getUTCFullYear() === payPeriodDate.getUTCFullYear() &&
      effectiveFromDate.getUTCMonth() === payPeriodDate.getUTCMonth()
    ) {
      targetDate = effectiveFromDate
    } else {
      targetDate = payPeriodDate
    }

    const lastDayOfMonth = new Date(
      Date.UTC(targetDate.getUTCFullYear(), targetDate.getUTCMonth() + 1, 0)
    ).getUTCDate()

    // ✅ Remaining days = (last day of month - current day) + 1
    return lastDayOfMonth - targetDate.getUTCDate() + 1
  }

  formatPayPeriodFromDateString(dateString: string): string {
    const date = new Date(dateString + 'T00:00:00Z')
    return date.toLocaleDateString('default', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  }

  //public async downloadAsPDF(): Promise<void> {
  //  if (!this.payrollData) {
  //    alert('Cannot generate PDF: Payslip data is missing.')
  //    return
  //  }

  //  const originalElement = this.payslipContainerToExport.nativeElement
  //  const clonedElement = originalElement.cloneNode(true) as HTMLElement

  //  // Style the clone for PDF generation:
  //  // - Position it off-screen to avoid affecting the live view.
  //  // - Give it a fixed, wide width to ensure Bootstrap columns render side-by-side.
  //  // - Ensure its full height is available.
  //  this.renderer.setStyle(clonedElement, 'position', 'absolute')
  //  this.renderer.setStyle(clonedElement, 'left', '-9999px')
  //  this.renderer.setStyle(clonedElement, 'top', '-9999px')
  //  this.renderer.setStyle(clonedElement, 'width', '1050px') // A suitable width for A4 rendering, ensures 'md' cols are side-by-side
  //  this.renderer.setStyle(clonedElement, 'maxWidth', '1050px')
  //  // If content might be clipped, ensure overflow is visible or set an explicit large height
  //  // this.renderer.setStyle(clonedElement, 'height', 'auto'); // or a calculated scrollHeight if needed
  //  // this.renderer.setStyle(clonedElement, 'overflow', 'visible');

  //  // Temporarily append the clone to the body so html2canvas can access its computed styles
  //  this.renderer.appendChild(document.body, clonedElement)

  //  try {
  //    const canvas = await html2canvas(clonedElement, {
  //      scale: 2, // Higher scale for better PDF quality
  //      useCORS: true,
  //      logging: true,
  //      backgroundColor: '#ffffff' // Ensures transparent backgrounds are white
  //      // Let html2canvas determine width/height from the styled clone.
  //      // Explicitly setting windowWidth/Height might be needed if auto-detection fails for complex layouts.
  //      // windowWidth: clonedElement.scrollWidth,
  //      // windowHeight: clonedElement.scrollHeight,
  //    })

  //    const imgWidthMM = 190 // A4 width (210mm) - 10mm margin on each side

  //    // Calculate image height in mm maintaining aspect ratio
  //    const imgHeightMM = (canvas.height * imgWidthMM) / canvas.width

  //    const contentDataURL = canvas.toDataURL('image/png', 1.0) // Quality 1.0
  //    const pdf = new jsPDF('p', 'mm', 'a4')

  //    const pdfPageWidth = pdf.internal.pageSize.getWidth()
  //    const pdfPageHeight = pdf.internal.pageSize.getHeight() // jsPDF's internal page height

  //    const pdfTopMargin = 10 // Top margin in PDF

  //    // Check if the image fits on one page
  //    if (imgHeightMM <= pdfPageHeight - pdfTopMargin - 10 /* bottom margin */) {
  //      pdf.addImage(
  //        contentDataURL,
  //        'PNG',
  //        (pdfPageWidth - imgWidthMM) / 2,
  //        pdfTopMargin,
  //        imgWidthMM,
  //        imgHeightMM
  //      )
  //    } else {
  //      // Handle multi-page PDF
  //      // This is a more robust way to handle multi-page by segmenting the canvas
  //      let currentYCanvasPx = 0 // Current Y position in the source canvas (in pixels)
  //      let remainingImageHeightMM = imgHeightMM
  //      let firstPage = true

  //      while (remainingImageHeightMM > 0) {
  //        if (!firstPage) {
  //          pdf.addPage()
  //        }

  //        const pageChunkHeightMM = firstPage
  //          ? Math.min(
  //              remainingImageHeightMM,
  //              pdfPageHeight - pdfTopMargin - 10 /* bottom margin */
  //            )
  //          : Math.min(
  //              remainingImageHeightMM,
  //              pdfPageHeight - 10 /* top */ - 10 /* bottom margin */
  //            )

  //        // Calculate the height of the segment in source canvas pixels
  //        const segmentSourceHeightPx = (pageChunkHeightMM / imgHeightMM) * canvas.height

  //        const tempCanvas = document.createElement('canvas')
  //        tempCanvas.width = canvas.width
  //        tempCanvas.height = segmentSourceHeightPx
  //        const tempCtx = tempCanvas.getContext('2d')

  //        if (tempCtx) {
  //          // Crop the original canvas and draw it onto the temporary canvas
  //          tempCtx.drawImage(
  //            canvas, // Source HTML Canvas element
  //            0, // Source X
  //            currentYCanvasPx, // Source Y (where to start cropping from the original canvas)
  //            canvas.width, // Source Width
  //            segmentSourceHeightPx, // Source Height (how much to crop for this page)
  //            0, // Destination X on tempCanvas
  //            0, // Destination Y on tempCanvas
  //            canvas.width, // Destination Width on tempCanvas
  //            segmentSourceHeightPx // Destination Height on tempCanvas
  //          )

  //          const segmentDataURL = tempCanvas.toDataURL('image/png', 1.0)
  //          pdf.addImage(
  //            segmentDataURL,
  //            'PNG',
  //            (pdfPageWidth - imgWidthMM) / 2,
  //            firstPage ? pdfTopMargin : 10,
  //            imgWidthMM,
  //            pageChunkHeightMM
  //          )
  //        }

  //        currentYCanvasPx += segmentSourceHeightPx
  //        remainingImageHeightMM -= pageChunkHeightMM
  //        firstPage = false
  //      }
  //    }

  //    const empName = this.payrollData?.employee.firstName?.replace(/\s+/g, '_').trim() ?? ''
  //    const empId = this.payrollData?.employee.empId?.replace(/\s+/g, '_').trim() ?? ''
  //    const yearMonth = getYearMonthString(new Date())

  //    pdf.save(`Payslip_${empName}_${empId}_${yearMonth}.pdf`)
  //    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  //  } catch (error) {
  //    //Log the error
  //    //console.error('Error generating PDF:', error)
  //    alert('Failed to generate PDF.')
  //  } finally {
  //    // Always remove the clone from the body
  //    this.renderer.removeChild(document.body, clonedElement)
  //  }
  //}

  //public async downloadAsPDF(): Promise<void> {
  //  if (!this.payrollData) {
  //    alert('Cannot generate PDF: Payslip data is missing.')
  //    return
  //  }

  //  const originalElement = this.payslipContainerToExport.nativeElement
  //  const clonedElement = originalElement.cloneNode(true) as HTMLElement

  //  // --- TEMPORARY DEBUGGING STYLES ---
  //  this.renderer.setStyle(clonedElement, 'position', 'fixed') // Use 'fixed' to see it
  //  this.renderer.setStyle(clonedElement, 'left', '0px')
  //  this.renderer.setStyle(clonedElement, 'top', '0px')
  //  this.renderer.setStyle(clonedElement, 'z-index', '9999') // Bring to front
  //  this.renderer.setStyle(clonedElement, 'border', '5px solid red') // Make it obvious
  //  // --- END TEMPORARY DEBUGGING STYLES ---

  //  // 1. ADD THE SPECIAL CSS CLASS to trigger our fixed-layout styles.
  //  this.renderer.addClass(clonedElement, 'pdf-export-container')

  //  // 2. STYLE THE CLONE to be invisible and off-screen.
  //  // The width is now controlled by our CSS class, so we don't set it here.
  //  //this.renderer.setStyle(clonedElement, 'position', 'absolute')
  //  //this.renderer.setStyle(clonedElement, 'left', '-9999px')
  //  //this.renderer.setStyle(clonedElement, 'top', '0px')

  //  // Temporarily append the clone to the body so its styles can be computed
  //  this.renderer.appendChild(document.body, clonedElement)
  //  debugger
  //  try {
  //    // html2canvas will now render the element with our fixed, desktop-like layout
  //    const canvas = await html2canvas(clonedElement, {
  //      scale: 2, // Higher scale for better PDF quality
  //      useCORS: true,
  //      logging: true,
  //      backgroundColor: '#ffffff' // Ensure white background
  //    })

  //    const imgWidthMM = 190 // A4 width (210mm) - 10mm margin on each side
  //    const imgHeightMM = (canvas.height * imgWidthMM) / canvas.width
  //    const contentDataURL = canvas.toDataURL('image/png', 1.0)
  //    const pdf = new jsPDF('p', 'mm', 'a4')

  //    const pdfPageWidth = pdf.internal.pageSize.getWidth()
  //    const pdfPageHeight = pdf.internal.pageSize.getHeight()
  //    const pdfTopMargin = 10

  //    // The robust multi-page logic you already have (no changes needed here)
  //    if (imgHeightMM <= pdfPageHeight - pdfTopMargin - 10 /* bottom margin */) {
  //      pdf.addImage(
  //        contentDataURL,
  //        'PNG',
  //        (pdfPageWidth - imgWidthMM) / 2,
  //        pdfTopMargin,
  //        imgWidthMM,
  //        imgHeightMM
  //      )
  //    } else {
  //      let currentYCanvasPx = 0
  //      let remainingImageHeightMM = imgHeightMM
  //      let firstPage = true

  //      while (remainingImageHeightMM > 0) {
  //        if (!firstPage) {
  //          pdf.addPage()
  //        }
  //        const pageChunkHeightMM = firstPage
  //          ? Math.min(remainingImageHeightMM, pdfPageHeight - pdfTopMargin - 10)
  //          : Math.min(remainingImageHeightMM, pdfPageHeight - 10 - 10)
  //        const segmentSourceHeightPx = (pageChunkHeightMM / imgHeightMM) * canvas.height
  //        const tempCanvas = document.createElement('canvas')
  //        tempCanvas.width = canvas.width
  //        tempCanvas.height = segmentSourceHeightPx
  //        const tempCtx = tempCanvas.getContext('2d')
  //        if (tempCtx) {
  //          tempCtx.drawImage(
  //            canvas,
  //            0,
  //            currentYCanvasPx,
  //            canvas.width,
  //            segmentSourceHeightPx,
  //            0,
  //            0,
  //            canvas.width,
  //            segmentSourceHeightPx
  //          )
  //          const segmentDataURL = tempCanvas.toDataURL('image/png', 1.0)
  //          pdf.addImage(
  //            segmentDataURL,
  //            'PNG',
  //            (pdfPageWidth - imgWidthMM) / 2,
  //            firstPage ? pdfTopMargin : 10,
  //            imgWidthMM,
  //            pageChunkHeightMM
  //          )
  //        }
  //        currentYCanvasPx += segmentSourceHeightPx
  //        remainingImageHeightMM -= pageChunkHeightMM
  //        firstPage = false
  //      }
  //    }

  //    const empName = this.payrollData?.employee.firstName?.replace(/\s+/g, '_').trim() ?? ''
  //    const empId = this.payrollData?.employee.empId?.replace(/\s+/g, '_').trim() ?? ''
  //    const yearMonth = getYearMonthString(new Date(this.payPeriodStartDate))

  //    pdf.save(`Payslip_${empName}_${empId}_${yearMonth}.pdf`)
  //  } catch (error) {
  //    console.error('Error generating PDF:', error)
  //    alert('Failed to generate PDF.')
  //  } finally {
  //    // Always remove the clone from the body
  //    this.renderer.removeChild(document.body, clonedElement)
  //  }
  //}

  public async downloadAsPDF(): Promise<void> {
    if (!this.payrollData) {
      alert('Cannot generate PDF: Payslip data is missing.')
      return
    }

    // 1. GATHER ALL STYLES AND CONTENT HTML FROM THE MAIN DOCUMENT
    // This is done *before* creating the iframe.

    // Get all <style> and <link rel="stylesheet"> tags as HTML strings
    const stylesHtml = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML)
      .join('')

    // Get the HTML of the payslip container
    const contentHtml = this.payslipContainerToExport.nativeElement.outerHTML

    // Get the base URL of the current application. This is CRITICAL for resolving relative paths.
    const baseHref = document.baseURI

    // 2. CREATE THE IFRAME AND SET ITS SRCDOC
    const iframe = this.renderer.createElement('iframe')
    this.renderer.setStyle(iframe, 'position', 'absolute')
    this.renderer.setStyle(iframe, 'left', '-9999px')
    this.renderer.setStyle(iframe, 'top', '-9999px')
    this.renderer.setAttribute(iframe, 'width', '1200') // Set desktop viewport width
    this.renderer.setAttribute(iframe, 'height', '1200') // Set ample height

    // Construct the entire iframe document in a string.
    // The <base> tag is the key to fixing all relative URLs (for CSS, fonts, images).
    iframe.srcdoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <base href="${baseHref}">
          ${stylesHtml} 
          <style>
        /* PDF Export Specific Styles */
        body {
          font-size: 22px !important; /* Increase base font size */
        }
        table, td, th, h6 {
          font-size: 22px !important; /* For table content */
        }
      </style>
        </head>
        <body>
          ${contentHtml}
        </body>
      </html>
    `

    // 3. WAIT FOR THE IFRAME TO FULLY LOAD ALL ITS CONTENT
    // The 'onload' event fires ONLY after the srcdoc is parsed and all
    // linked resources (CSS, fonts) have been loaded. This replaces all manual promises.
    const promise = new Promise<HTMLIFrameElement>(resolve => {
      iframe.onload = () => resolve(iframe)
    })

    // Appending the iframe to the body triggers the loading process.
    this.renderer.appendChild(document.body, iframe)

    try {
      // Wait for the onload event
      await promise

      // Now the iframe is a perfect, fully-styled, desktop-width copy.
      const iframeDoc = iframe.contentWindow.document
      const elementToRender = iframeDoc.body.firstElementChild as HTMLElement

      // 4. RUN HTML2CANVAS ON THE IFRAME'S CONTENT
      const canvas = await html2canvas(elementToRender, {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff'
      })

      // 5. GENERATE PDF
      const imgWidthMM = 190
      const imgHeightMM = (canvas.height * imgWidthMM) / canvas.width
      const contentDataURL = canvas.toDataURL('image/png', 1.0)
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfPageWidth = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      const pdfTopMargin = 10

      // Multi-page logic
      if (imgHeightMM <= pdfPageHeight - pdfTopMargin - 10) {
        pdf.addImage(
          contentDataURL,
          'PNG',
          (pdfPageWidth - imgWidthMM) / 2,
          pdfTopMargin,
          imgWidthMM,
          imgHeightMM
        )
      } else {
        let currentYCanvasPx = 0
        let remainingImageHeightMM = imgHeightMM
        let firstPage = true
        while (remainingImageHeightMM > 0) {
          if (!firstPage) {
            pdf.addPage()
          }
          const pageChunkHeightMM = firstPage
            ? Math.min(remainingImageHeightMM, pdfPageHeight - pdfTopMargin - 10)
            : Math.min(remainingImageHeightMM, pdfPageHeight - 10 - 10)
          const segmentSourceHeightPx = (pageChunkHeightMM / imgHeightMM) * canvas.height
          const tempCanvas = document.createElement('canvas')
          tempCanvas.width = canvas.width
          tempCanvas.height = segmentSourceHeightPx
          const tempCtx = tempCanvas.getContext('2d')
          if (tempCtx) {
            tempCtx.drawImage(
              canvas,
              0,
              currentYCanvasPx,
              canvas.width,
              segmentSourceHeightPx,
              0,
              0,
              canvas.width,
              segmentSourceHeightPx
            )
            const segmentDataURL = tempCanvas.toDataURL('image/png', 1.0)
            pdf.addImage(
              segmentDataURL,
              'PNG',
              (pdfPageWidth - imgWidthMM) / 2,
              firstPage ? pdfTopMargin : 10,
              imgWidthMM,
              pageChunkHeightMM
            )
          }
          currentYCanvasPx += segmentSourceHeightPx
          remainingImageHeightMM -= pageChunkHeightMM
          firstPage = false
        }
      }

      const empName = this.payrollData?.employee.firstName?.replace(/\s+/g, '_').trim() ?? ''
      const empId = this.payrollData?.employee.empId?.replace(/\s+/g, '_').trim() ?? ''
      const yearMonth = new Date(this.payPeriodStartDate).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit'
      })

      pdf.save(`Payslip_${empName}_${empId}_${yearMonth}.pdf`)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      alert('Failed to generate PDF.')
    } finally {
      // 6. ALWAYS CLEAN UP
      this.renderer.removeChild(document.body, iframe)
    }
  }

  // public async downloadPayslipPDF() {
  //   const elementToRender = document.getElementById('payslip-container') // wrap your HTML in a container div

  //   if (!elementToRender) {
  //     console.error('Payslip container not found!')
  //     return
  //   }

  //   const cloned = elementToRender.cloneNode(true) as HTMLElement

  //   // Make logo bigger in the cloned version
  //   const logo = cloned.querySelector('img') as HTMLImageElement
  //   if (logo) {
  //     logo.style.maxWidth = '350px'
  //     logo.style.height = 'auto'
  //     logo.classList.remove('img-fluid') // remove responsive constraint
  //   }

  //   // Append clone off-screen so html2canvas can capture it
  //   cloned.style.position = 'absolute'
  //   cloned.style.left = '-9999px'
  //   document.body.appendChild(cloned)

  //   const canvas = await html2canvas(cloned, { scale: 3, useCORS: true })
  //   document.body.removeChild(cloned)
  //   // Capture the HTML with better scaling
  //   // const canvas = await html2canvas(elementToRender, {
  //   //   scale: 4, // sharpness
  //   //   useCORS: true,
  //   //   backgroundColor: '#ffffff'
  //   // })

  //   const imgData = canvas.toDataURL('image/png')

  //   // Create PDF (A4 size)
  //   const pdf = new jsPDF('p', 'mm', 'a4')

  //   const pageWidth = pdf.internal.pageSize.getWidth()
  //   const pageHeight = pdf.internal.pageSize.getHeight()

  //   // Slight margin so it doesn't touch the edge
  //   const imgWidth = pageWidth - 10
  //   const imgHeight = (canvas.height * imgWidth) / canvas.width

  //   pdf.addImage(imgData, 'PNG', 5, 5, imgWidth, imgHeight)
  //   // pdf.save('payslip.pdf')
  //   const empName = this.payrollData?.employee.firstName?.replace(/\s+/g, '_').trim() ?? ''
  //   const empId = this.payrollData?.employee.empId?.replace(/\s+/g, '_').trim() ?? ''
  //   const yearMonth = new Date(this.payPeriodStartDate).toLocaleDateString('en-CA', {
  //     year: 'numeric',
  //     month: '2-digit'
  //   })

  //   pdf.save(`Payslip_${empName}_${empId}_${yearMonth}.pdf`)
  // }

  //gps 18 dec 2025
  public async downloadPayslipPDF() {
    const element = document.getElementById('payslip-container')
    if (!element) return

    const cloned = element.cloneNode(true) as HTMLElement

    /* FIXED DESKTOP LAYOUT */
    // cloned.style.width = '1200px'
    // cloned.style.minWidth = '1200px'
    // cloned.style.maxWidth = '1200px'
    const a4WidthPx = 794
    cloned.style.width = `${a4WidthPx}px`
    cloned.style.minWidth = `${a4WidthPx}px`
    cloned.style.maxWidth = `${a4WidthPx}px`

    cloned.style.fontSize = '12px'
    cloned.style.background = '#ffffff'
    cloned.style.boxSizing = 'border-box'

    /* REMOVE RESPONSIVE BEHAVIOR */
    // cloned.querySelectorAll('[class*="col-"]').forEach(el => {
    //   el.className = el.className
    //     .split(' ')
    //     .filter(c => !/^col-(xs|sm|md|lg|xl)/.test(c))
    //     .join(' ')
    // })

    cloned.querySelectorAll('img').forEach(img => {
      img.classList.remove('img-fluid')
    })

    /* OFFSCREEN RENDER */
    cloned.style.position = 'absolute'
    cloned.style.left = '-9999px'
    document.body.appendChild(cloned)

    const canvas = await html2canvas(cloned, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 1200,
      // windowWidth: a4WidthPx,
      devicePixelRatio: 1
    } as any)

    document.body.removeChild(cloned)

    /* A4 PDF */
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    const margin = 10 // mm
    const imgWidth = pageWidth - margin * 2
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    let y = margin

    /* HANDLE MULTI-PAGE */
    let heightLeft = imgHeight

    while (heightLeft > 0) {
      pdf.addImage(canvas, 'PNG', margin, y, imgWidth, imgHeight)
      heightLeft -= pageHeight - margin * 2
      if (heightLeft > 0) {
        pdf.addPage()
        y = margin
      }
    }

    const empName = this.payrollData?.employee?.firstName ?? ''
    const empId = this.payrollData?.employee?.empId ?? ''
    const yearMonth = new Date(this.payPeriodStartDate).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit'
    })

    pdf.save(`Payslip_${empName}_${empId}_${yearMonth}.pdf`)
  }

  get lopDeductionAmount(): number {
    if (!this.payrollData?.calculationBreakdown?.deductions) {
      return 0
    }

    return this.payrollData.calculationBreakdown.deductions
      .filter(d => d.name.toLowerCase().startsWith('lop deduction'))
      .reduce((sum, d) => sum + d.amount, 0)
  }
}
